import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentResult({ cancelled = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");

  useEffect(() => {
    if (cancelled || !sessionId) {
      if (!cancelled) setState("error");
      return undefined;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState("paid");
        if (data.status === "expired") return setState("error");
      } catch { /* retry */ }
      tries += 1;
      if (tries < 15) timer = setTimeout(poll, 2000);
      else setState("pending");
      return undefined;
    };
    poll();
    return () => clearTimeout(timer);
  }, [cancelled, sessionId]);

  const VIEW = {
    checking: { icon: <Loader2 className="h-10 w-10 text-primary animate-spin" />, title: "Confirming your payment…", text: "This only takes a moment." },
    paid: { icon: <CheckCircle2 className="h-10 w-10 text-emerald-400" />, title: "Payment received — thank you!", text: "Your dispute letters to Equifax, Experian and TransUnion will now be printed and sent by certified mail. You can close this page." },
    pending: { icon: <Loader2 className="h-10 w-10 text-amber-400" />, title: "Payment is processing", text: "We haven't received confirmation yet. Your advisor will be notified as soon as it clears." },
    cancelled: { icon: <XCircle className="h-10 w-10 text-amber-400" />, title: "Payment cancelled", text: "No charge was made. You can use the same link from your email to pay whenever you're ready." },
    error: { icon: <XCircle className="h-10 w-10 text-destructive" />, title: "We couldn't confirm this payment", text: "Please contact your advisor at Urbanvision Financial Solutions for a new payment link." },
  }[state];

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <Card className="p-8 max-w-md w-full bg-card/70 border-border text-center animate-fade-up" data-testid={`payment-result-${state}`}>
        <div className="flex justify-center mb-4">{VIEW.icon}</div>
        <p className="text-xs tracking-[0.2em] uppercase text-primary font-bold mb-2">Urbanvision Financial Solutions</p>
        <h1 className="font-display text-2xl font-bold" data-testid="payment-result-title">{VIEW.title}</h1>
        <p className="text-sm text-muted-foreground mt-3">{VIEW.text}</p>
      </Card>
    </div>
  );
}
